/**
 * Voice API Service
 * Uses the shared apiClient from api.ts (includes token refresh interceptor)
 */

import { apiClient } from './api';

export interface TokenResponse {
  token: string;
  url: string;
  roomName: string;
  conversationId: string;
}

/**
 * #1731 (B-1): `conversationId` names the thread to reuse; `sourceConversationId`
 * names the text thread the call grows out of, which the route packs into the
 * room metadata as `handoffFrom` for the voice agent. On TRF they are usually
 * the same id (one record carries both modalities).
 */
export interface VoiceTokenParams {
  conversationId?: string;
  sourceConversationId?: string;
  /**
   * #1731 phase 6: return me to the room I just dropped, if one is still open.
   * Only the re-attach path sets this. Without it the route keeps its 409
   * duplicate-session mutex, which is what stops a second tab opening a second
   * concurrent call.
   */
  rejoin?: boolean;
}

export const voiceApi = {
  /**
   * POST /api/voice/token
   * Request a LiveKit room token for a voice session.
   * Voice config is resolved server-side from the user's saved preferences.
   * Checks mic permission BEFORE calling this — orphaned rooms are expensive.
   */
  getToken: async (params?: VoiceTokenParams): Promise<TokenResponse> => {
    const body: Record<string, string | boolean> = {};
    if (params?.conversationId) body.conversationId = params.conversationId;
    if (params?.sourceConversationId) body.sourceConversationId = params.sourceConversationId;
    if (params?.rejoin) body.rejoin = true;
    const response = await apiClient.post<{ status: string; data: TokenResponse }>(
      '/voice/token',
      body
    );
    return response.data.data;
  },

  /**
   * DELETE /api/voice/session
   * Explicitly clear the active-session Redis key so the user can start a new session.
   * Primary cleanup path — webhooks can't reach localhost in dev.
   */
  endSession: async (): Promise<void> => {
    await apiClient.delete('/voice/session');
  },

  /**
   * POST /api/trfer/voice/token
   * TRFer-side equivalent of getToken — dispatches the williams-voice agent
   * into a separate room namespace, with its own Redis active-session key.
   */
  getTrferToken: async (params?: VoiceTokenParams & { familyId?: string }): Promise<TokenResponse> => {
    const body: Record<string, string> = {};
    if (params?.conversationId) body.conversationId = params.conversationId;
    if (params?.sourceConversationId) body.sourceConversationId = params.sourceConversationId;
    if (params?.familyId) body.familyId = params.familyId;
    const response = await apiClient.post<{ status: string; data: TokenResponse }>(
      '/trfer/voice/token',
      body
    );
    return response.data.data;
  },

  /**
   * DELETE /api/trfer/voice/session
   * Clear the TRFer-side active-session Redis key (trfer-voice:active:<userId>).
   */
  endTrferSession: async (): Promise<void> => {
    await apiClient.delete('/trfer/voice/session');
  },
};
